import { inject, Injectable } from '@angular/core';
import { MessageService } from '@progress/kendo-angular-l10n';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
// Provides French UI strings for Kendo components when Office locale is French
// Date/Time uses Maximizer database locale via LocaleService
export class FrenchMessageService extends MessageService {
  private readonly translate = inject(TranslateService);

  private readonly kendoToTranslationKeyMap: { [key: string]: string } = {
    // DateTimePicker messages
    'kendo.datetimepicker.today': 'outlook.shared.component.today',
    'kendo.datetimepicker.now': 'outlook.shared.component.now',
    'kendo.datetimepicker.accept': 'outlook.buttons.set',
    'kendo.datetimepicker.cancel': 'outlook.buttons.cancel',
    'kendo.datetimepicker.date': 'outlook.shared.component.date',
    'kendo.datetimepicker.time': 'outlook.shared.component.time',
    'kendo.datetimepicker.dateTab': 'outlook.shared.component.dateTab',
    'kendo.datetimepicker.dateTabLabel':
      'outlook.shared.component.dateTabLabel',
    'kendo.datetimepicker.timeTab': 'outlook.shared.component.timeTab',
    'kendo.datetimepicker.timeTabLabel':
      'outlook.shared.component.timeTabLabel',
    'kendo.datetimepicker.toggle': 'outlook.shared.component.toggle',
    'kendo.datetimepicker.nowLabel': 'outlook.shared.component.nowLabel',
    'kendo.datetimepicker.hour': 'outlook.shared.component.hour',
    'kendo.datetimepicker.minute': 'outlook.shared.component.minute',
    'kendo.datetimepicker.second': 'outlook.shared.component.second',
    'kendo.datetimepicker.millisecond': 'outlook.shared.component.millisecond',
    'kendo.datetimepicker.prevButtonTitle':
      'outlook.shared.component.prevButtonTitle',
    'kendo.datetimepicker.nextButtonTitle':
      'outlook.shared.component.nextButtonTitle',
    'kendo.datetimepicker.parentViewButtonTitle':
      'outlook.shared.component.parentViewButtonTitle',
    'kendo.datetimepicker.clearTitle': 'outlook.shared.component.clearTitle',
    'kendo.datetimepicker.adaptiveCloseButtonTitle':
      'outlook.shared.component.adaptiveCloseButtonTitle',

    // DatePicker messages
    'kendo.datepicker.today': 'outlook.shared.component.today',
    'kendo.datepicker.accept': 'outlook.buttons.set',
    'kendo.datepicker.cancel': 'outlook.buttons.cancel',
    'kendo.datepicker.toggle': 'outlook.shared.component.toggle',

    // TimePicker messages
    'kendo.timepicker.now': 'outlook.shared.component.now',
    'kendo.timepicker.cancel': 'outlook.buttons.cancel',
    'kendo.timepicker.accept': 'outlook.buttons.set',
    'kendo.timepicker.acceptLabel': 'outlook.buttons.accept',
    'kendo.timepicker.cancelLabel': 'outlook.buttons.cancel',
    'kendo.timepicker.nowLabel': 'outlook.shared.component.nowLabel',

    // Calendar messages
    'kendo.calendar.today': 'outlook.shared.component.today',
    'kendo.calendar.navigate': 'outlook.shared.component.navigate',
    'kendo.calendar.viewMonth': 'outlook.shared.component.viewMonth',
    'kendo.calendar.viewYear': 'outlook.shared.component.viewYear',
    'kendo.calendar.viewDecade': 'outlook.shared.component.viewDecade',
    'kendo.calendar.viewCentury': 'outlook.shared.component.viewCentury',
    'kendo.calendar.weekColumnHeader':
      'outlook.shared.component.weekColumnHeader',

    // Common button messages
    'kendo.dialog.ok': 'outlook.buttons.ok',
    'kendo.dialog.cancel': 'outlook.buttons.cancel',
    'kendo.dialog.yes': 'outlook.buttons.yes',
    'kendo.dialog.no': 'outlook.buttons.no',
    'kendo.dialog.set': 'outlook.buttons.set',
    'kendo.dialog.save': 'outlook.buttons.save',

    // Pager messages
    'kendo.pager.page': 'outlook.shared.component.page',
    'kendo.pager.of': 'outlook.shared.component.of',
    'kendo.pager.items': 'outlook.shared.component.items',
    'kendo.pager.itemsPerPage': 'outlook.shared.component.itemsPerPage',
    'kendo.pager.first': 'outlook.shared.component.first',
    'kendo.pager.firstPage': 'outlook.shared.component.firstPage',
    'kendo.pager.previous': 'outlook.shared.component.previous',
    'kendo.pager.previousPage': 'outlook.shared.component.previousPage',
    'kendo.pager.next': 'outlook.shared.component.next',
    'kendo.pager.nextPage': 'outlook.shared.component.nextPage',
    'kendo.pager.last': 'outlook.shared.component.last',
    'kendo.pager.lastPage': 'outlook.shared.component.lastPage',

    // Dropdown messages
    'kendo.dropdownlist.noDataText': 'outlook.forms.noDataFound',

    // MultiSelectTree messages
    'kendo.multiselecttree.checkAllText': 'outlook.filter.checkAll',
    'kendo.multiselecttree.checkAll': 'outlook.filter.checkAll',
  };

  public override get(key: string): string | undefined {
    const translationKey = this.kendoToTranslationKeyMap[key];
    if (translationKey) {
      const translated = this.translate.instant(translationKey);
      return translated !== translationKey ? translated : undefined;
    }
    return undefined;
  }
}
